<template>
  <v-card>
    <v-card-title class="body-2 v-card__subtitle">
      <v-icon x-small class="mr-2">mdi-chart-timeline</v-icon
      >Abrechnungskontingente

      <v-spacer></v-spacer>
      <v-menu
        v-if="!disableDatePicker"
        v-model="datePickerMenu"
        :close-on-content-click="false"
        offset-y
        left
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn depressed small v-bind="attrs" v-on="on" class="my-0 py-0">
            <v-icon left small>mdi-calendar</v-icon>
            {{ formatDate(referenceDate) }}
            <v-icon right small>mdi-menu-down</v-icon>
          </v-btn>
        </template>
        <v-date-picker
          v-model="referenceDate"
          @input="onReferenceDateChange"
          locale="de-DE"
          first-day-of-week="1"
          :max="todayISO"
        ></v-date-picker>
      </v-menu>
      <v-btn
        icon
        x-small
        class="ml-1 my-0 py-0"
        @click="refreshQuotaData"
        :loading="loading"
      >
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text v-if="loading" class="text-center py-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2 text-caption grey--text">
        Kontingente werden geladen...
      </div>
    </v-card-text>
    <v-card-text v-else-if="quotaItems.length === 0" class="text-center py-4">
      <v-icon large color="grey lighten-1">mdi-chart-timeline</v-icon>
      <div class="mt-2 grey--text">
        Keine Abrechnungsarten mit Kontingent konfiguriert.
      </div>
    </v-card-text>
    <v-list v-else class="py-0">
      <v-list-item
        v-for="(item, index) in quotaItems"
        :key="item.payrollType.id"
        :class="[
          { 'border-bottom': index < quotaItems.length - 1 },
          pendingUsageByType[item.payrollType.id] ? 'quota-with-pending' : '',
        ]"
        class="px-3 py-2"
        dense
      >
        <v-list-item-content class="py-1">
          <v-row
            no-gutters
            class="justify-center justify-space-between align-center"
          >
            <v-col cols="8" sm="6">
              <v-list-item-title class="subtitle-2">
                {{ item.payrollType.title }}
              </v-list-item-title>
              <v-list-item-subtitle>
                <div class="text-caption grey--text">
                  {{ item.quota.allowedMax }}
                  {{ formatQuotaUnit(item.quota.maxUnit) }} pro
                  {{ formatTimeUnit(item.quota.timeUnit) }} erlaubt von
                  <br class="hidden-sm-and-up" />{{
                    formatDate(item.periodStart)
                  }}
                  bis
                  {{ formatDate(item.periodEnd) }}
                </div>
                <div
                  v-if="pendingUsageByType[item.payrollType.id]"
                  :class="[
                    'text-caption mt-1',
                    item.currentUsage +
                      pendingUsageByType[item.payrollType.id] >
                    item.quota.allowedMax
                      ? 'red--text text--darken-2'
                      : 'orange--text text--darken-3',
                  ]"
                >
                  voraussichtlich +
                  {{
                    formatUsageValue(
                      pendingUsageByType[item.payrollType.id],
                      item.quota.maxUnit,
                    )
                  }}
                  {{ formatQuotaUnit(item.quota.maxUnit) }} aus diesem
                  Dienstbericht
                </div>
              </v-list-item-subtitle>
            </v-col>
            <v-col cols="4" sm="6">
              <v-progress-linear
                :value="
                  pendingUsageByType[item.payrollType.id]
                    ? Math.min(
                        ((item.currentUsage +
                          pendingUsageByType[item.payrollType.id]) /
                          item.quota.allowedMax) *
                          100,
                        100,
                      )
                    : item.percentageUsed
                "
                :color="
                  pendingUsageByType[item.payrollType.id]
                    ? getProgressColorWithPending(
                        item,
                        pendingUsageByType[item.payrollType.id],
                      )
                    : getProgressColor(item)
                "
                height="26"
                rounded
                :class="
                  pendingUsageByType[item.payrollType.id]
                    ? 'quota-preview-progress'
                    : ''
                "
              >
                <template v-slot:default>
                  <div
                    v-if="pendingUsageByType[item.payrollType.id]"
                    class="d-flex align-center"
                    style="width: 100%; position: relative"
                  >
                    <!-- Current usage segment -->
                    <div
                      :style="{
                        width:
                          Math.min(
                            (item.currentUsage / item.quota.allowedMax) * 100,
                            100,
                          ) + '%',
                        background: 'rgba(255, 255, 255, 0.35)',
                        height: '100%',
                        position: 'absolute',
                        left: 0,
                        borderRadius:
                          item.currentUsage >= item.quota.allowedMax
                            ? '12px'
                            : '12px 0 0 12px',
                      }"
                    ></div>
                    <!-- Text overlay -->
                    <span
                      class="text-caption white--text font-weight-medium"
                      style="
                        font-size: 10px !important;
                        position: relative;
                        z-index: 1;
                        margin: 0 auto;
                      "
                    >
                      {{
                        formatUsageValue(item.currentUsage, item.quota.maxUnit)
                      }}
                      <span style="opacity: 0.7">+</span>
                      {{
                        formatUsageValue(
                          pendingUsageByType[item.payrollType.id],
                          item.quota.maxUnit,
                        )
                      }}
                      <span style="opacity: 0.7">=</span>
                      {{
                        formatUsageValue(
                          item.currentUsage +
                            pendingUsageByType[item.payrollType.id],
                          item.quota.maxUnit,
                        )
                      }}
                      / {{ item.quota.allowedMax }}
                      {{ formatQuotaUnit(item.quota.maxUnit) }}
                    </span>
                  </div>
                  <span
                    v-else
                    class="text-caption white--text font-weight-medium"
                    style="font-size: 10px !important"
                  >
                    {{
                      formatUsageValue(item.currentUsage, item.quota.maxUnit)
                    }}
                    /
                    {{ item.quota.allowedMax }}
                    {{ formatQuotaUnit(item.quota.maxUnit) }}
                  </span>
                </template>
              </v-progress-linear>
            </v-col>
          </v-row>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-card>
</template>

<script>
import { db, Timestamp } from "@/firebase";

export default {
  name: "quota-checker",
  props: {
    // User ID to check quotas for - if not provided, checks for current user
    userId: {
      type: String,
      default: null,
    },
    // Pending attendances to preview quota impact
    pendingAttendances: {
      type: Array,
      default: () => [],
    },
    // Whether to auto-load on mount
    autoLoad: {
      type: Boolean,
      default: true,
    },
    // Compact display mode
    compact: {
      type: Boolean,
      default: false,
    },
    // Initial reference date (Timestamp or Date object)
    initialDate: {
      type: [Object, Date],
      default: null,
    },
    // Disable date picker (fixed date mode)
    disableDatePicker: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      datePickerMenu: false,
      referenceDate: this.getInitialReferenceDate(),
      quotaData: [],
    };
  },
  computed: {
    organizationId() {
      return this.$route.params.organizationId;
    },
    payrollTypes() {
      return this.$store.state.accounting.payrollTypes || [];
    },
    payrollTypesWithQuota() {
      return this.payrollTypes.filter(
        (pt) => pt.quota && pt.quota.isApplicable,
      );
    },
    todayISO() {
      return this.formatDateISO(new Date());
    },
    quotaItems() {
      return this.payrollTypesWithQuota.map((payrollType) => {
        const quotaInfo = this.quotaData.find(
          (q) => q.payrollTypeId === payrollType.meta.id,
        );
        const currentUsage = quotaInfo ? quotaInfo.totalUsage : 0;
        const allowedMax = payrollType.quota.allowedMax || 0;
        const percentageUsed =
          allowedMax > 0 ? (currentUsage / allowedMax) * 100 : 0;

        // Calculate period based on quota's timeUnit
        const period = this.calculatePeriodForQuota(payrollType.quota.timeUnit);

        return {
          payrollType: {
            id: payrollType.meta.id,
            title: payrollType.title,
            shortTitle: payrollType.shortTitle,
            color: payrollType.color,
          },
          quota: payrollType.quota,
          periodStart: period.start,
          periodEnd: period.end,
          currentUsage,
          percentageUsed: Math.min(percentageUsed, 100),
          isOverLimit: currentUsage > allowedMax,
          isAtLimit: currentUsage === allowedMax,
          isNearLimit: percentageUsed >= 80 && percentageUsed < 100,
        };
      });
    },
    pendingUsageByType() {
      if (!this.pendingAttendances || this.pendingAttendances.length === 0) {
        return {};
      }

      const pendingUsageByType = {};
      this.pendingAttendances.forEach((attendance) => {
        if (
          attendance.payrollType &&
          attendance.payrollType.id &&
          attendance.attendanceStatus === "1" // Only count present attendees
        ) {
          const ptId = attendance.payrollType.id;
          if (!pendingUsageByType[ptId]) {
            pendingUsageByType[ptId] = 0;
          }
          // Calculate duration in hours/minutes
          const duration = this.calculateAttendanceDuration(attendance);
          pendingUsageByType[ptId] += duration;
        }
      });

      return pendingUsageByType;
    },
    quotaItemsWithPending() {
      if (!this.pendingAttendances || this.pendingAttendances.length === 0) {
        return [];
      }

      // Only return items that have pending usage
      return this.quotaItems
        .filter((item) => this.pendingUsageByType[item.payrollType.id])
        .map((item) => {
          const pendingUsage =
            this.pendingUsageByType[item.payrollType.id] || 0;
          const totalUsageWithPending = item.currentUsage + pendingUsage;
          const allowedMax = item.quota.allowedMax || 0;
          const percentageUsedWithPending =
            allowedMax > 0 ? (totalUsageWithPending / allowedMax) * 100 : 0;

          return {
            ...item,
            pendingUsage,
            totalUsageWithPending,
            percentageUsedWithPending: Math.min(percentageUsedWithPending, 100),
            isOverLimitWithPending: totalUsageWithPending > allowedMax,
            isAtLimitWithPending: totalUsageWithPending === allowedMax,
            isNearLimitWithPending:
              percentageUsedWithPending >= 80 &&
              percentageUsedWithPending < 100,
          };
        });
    },
  },
  watch: {
    pendingAttendances: {
      handler() {
        // Recalculate when pending attendances change
        this.$forceUpdate();
      },
      deep: true,
    },
    // Watch for payroll types to be loaded from store
    payrollTypesWithQuota: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && this.quotaData.length === 0) {
          this.refreshQuotaData();
        }
      },
      immediate: true,
    },
  },
  created() {
    if (this.autoLoad) {
      this.initialize();
    }
  },
  methods: {
    getInitialReferenceDate() {
      if (this.initialDate) {
        // Handle Firestore Timestamp
        if (
          this.initialDate.toDate &&
          typeof this.initialDate.toDate === "function"
        ) {
          return this.formatDateISO(this.initialDate.toDate());
        }
        // Handle regular Date object
        if (this.initialDate instanceof Date) {
          return this.formatDateISO(this.initialDate);
        }
      }
      return this.formatDateISO(new Date());
    },
    async initialize() {
      await this.refreshQuotaData();
    },
    calculatePeriodForQuota(timeUnit) {
      const refDate = new Date(this.referenceDate + "T00:00:00");
      let start, end;

      switch (timeUnit) {
        case "week": {
          // Get Monday of the week containing referenceDate
          const dayOfWeek = refDate.getDay();
          const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Handle Sunday
          start = new Date(refDate);
          start.setDate(refDate.getDate() + diff);
          start.setHours(0, 0, 0, 0);
          // Get Sunday of the week
          end = new Date(start);
          end.setDate(start.getDate() + 6);
          end.setHours(23, 59, 59, 999);
          break;
        }
        case "month": {
          // Get first day of the month containing referenceDate
          start = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
          // Get last day of the month
          end = new Date(
            refDate.getFullYear(),
            refDate.getMonth() + 1,
            0,
            23,
            59,
            59,
            999,
          );
          break;
        }
        case "quarter": {
          // Get the quarter containing referenceDate
          const quarter = Math.floor(refDate.getMonth() / 3);
          start = new Date(refDate.getFullYear(), quarter * 3, 1);
          end = new Date(
            refDate.getFullYear(),
            quarter * 3 + 3,
            0,
            23,
            59,
            59,
            999,
          );
          break;
        }
        case "year": {
          // Get the year containing referenceDate
          start = new Date(refDate.getFullYear(), 0, 1);
          end = new Date(refDate.getFullYear(), 11, 31, 23, 59, 59, 999);
          break;
        }
        default:
          start = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
          end = new Date(
            refDate.getFullYear(),
            refDate.getMonth() + 1,
            0,
            23,
            59,
            59,
            999,
          );
      }

      return {
        start: this.formatDateISO(start),
        end: this.formatDateISO(end),
      };
    },
    formatDateISO(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr + "T00:00:00");
      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    onReferenceDateChange() {
      this.datePickerMenu = false;
      this.refreshQuotaData();
    },
    async refreshQuotaData() {
      if (!this.organizationId || this.payrollTypesWithQuota.length === 0) {
        return;
      }

      this.loading = true;
      try {
        const timeSheetsRef = db
          .collection("organizations")
          .doc(this.organizationId)
          .collection("timeSheets");

        // Query for each payroll type with its individual period
        const quotaUsage = await Promise.all(
          this.payrollTypesWithQuota.map(async (payrollType) => {
            try {
              // Calculate period based on this payrollType's timeUnit
              const period = this.calculatePeriodForQuota(
                payrollType.quota.timeUnit,
              );
              const startDate = Timestamp.fromDate(
                new Date(period.start + "T00:00:00"),
              );
              const endDate = Timestamp.fromDate(
                new Date(period.end + "T23:59:59"),
              );

              // Build base query - Firestore has limitations on compound queries
              // We need to filter some conditions client-side to avoid index requirements
              let query = timeSheetsRef
                .where("payrollType.id", "==", payrollType.meta.id)
                .where("status", "in", [
                  "toBeProcessed",
                  "inProgress",
                  "toBeReviewed",
                  "approved",
                ]);

              // Filter by user if specified
              if (this.userId) {
                query = query.where("user.uid", "==", this.userId);
              }

              const snapshot = await query.get();

              let totalHours = 0;
              let entryCount = 0;

              snapshot.forEach((doc) => {
                const docData = doc.data();

                // Client-side date filtering to avoid complex composite index
                const docTimestamp = docData.billableDuration?.start?.timestamp;
                if (docTimestamp) {
                  const docDate = docTimestamp.toDate();
                  const startDateTime = startDate.toDate();
                  const endDateTime = endDate.toDate();

                  if (docDate >= startDateTime && docDate <= endDateTime) {
                    if (
                      docData.billableDuration &&
                      docData.billableDuration.inHours
                    ) {
                      totalHours += docData.billableDuration.inHours;
                    }
                    entryCount++;
                  }
                }
              });

              return {
                payrollTypeId: payrollType.meta.id,
                totalUsage: totalHours,
                entryCount,
              };
            } catch (queryError) {
              console.error(
                `Error querying quota for payrollType ${payrollType.meta.id}:`,
                queryError,
              );
              return {
                payrollTypeId: payrollType.meta.id,
                totalUsage: 0,
                entryCount: 0,
                error: queryError.message,
              };
            }
          }),
        );

        this.quotaData = quotaUsage;
      } catch (error) {
        console.error("Error fetching quota data:", error);
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },
    calculateAttendanceDuration(attendance) {
      // Calculate duration based on quota unit (hours or count)
      if (
        attendance.duration &&
        attendance.duration.start &&
        attendance.duration.end
      ) {
        const startDateTime = new Date(
          `${attendance.duration.start.date}T${attendance.duration.start.time}`,
        );
        const endDateTime = new Date(
          `${attendance.duration.end.date}T${attendance.duration.end.time}`,
        );

        if (
          !isNaN(startDateTime.getTime()) &&
          !isNaN(endDateTime.getTime()) &&
          endDateTime > startDateTime
        ) {
          // Return hours
          return (endDateTime - startDateTime) / (1000 * 60 * 60);
        }
      }
      return 0;
    },
    formatUsageValue(value, unit) {
      if (unit === "count") {
        return Math.round(value);
      }
      // For hours, show one decimal place
      return value.toFixed(0);
    },
    formatQuotaUnit(unit) {
      switch (unit) {
        case "hours":
          return "h";
        case "count":
          return "Einträge";
        case "days":
          return "Tage";
        default:
          return unit;
      }
    },
    formatTimeUnit(unit) {
      switch (unit) {
        case "currentDay":
          return "Tag";
        case "currentWeek":
          return "Woche";
        case "currentMonth":
          return "Monat";
        case "currentQuarter":
          return "Quartal";
        case "currentYear":
          return "Jahr";
        default:
          return unit;
      }
    },
    getProgressColor(item, withPending = false) {
      const isOver = withPending
        ? item.isOverLimitWithPending
        : item.isOverLimit;
      const isNear = withPending
        ? item.isNearLimitWithPending
        : item.isNearLimit;

      if (isOver) return "error";
      if (isNear) return "warning";
      return "success";
    },
    getProgressColorWithPending(item, pendingUsage) {
      const totalUsage = item.currentUsage + pendingUsage;
      const allowedMax = item.quota.allowedMax || 0;
      const percentageUsed =
        allowedMax > 0 ? (totalUsage / allowedMax) * 100 : 0;

      if (totalUsage > allowedMax) return "error";
      if (percentageUsed >= 80) return "warning";
      return "success";
    },
    getStatusChipColor(item, withPending = false) {
      const isOver = withPending
        ? item.isOverLimitWithPending
        : item.isOverLimit;
      const isNear = withPending
        ? item.isNearLimitWithPending
        : item.isNearLimit;
      const isAt = withPending ? item.isAtLimitWithPending : item.isAtLimit;

      if (isOver) return "red lighten-5";
      if (isAt) return "orange lighten-5";
      if (isNear) return "yellow lighten-4";
      return "green lighten-5";
    },
    getStatusChipTextColor(item, withPending = false) {
      const isOver = withPending
        ? item.isOverLimitWithPending
        : item.isOverLimit;
      const isNear = withPending
        ? item.isNearLimitWithPending
        : item.isNearLimit;
      const isAt = withPending ? item.isAtLimitWithPending : item.isAtLimit;

      if (isOver) return "error";
      if (isAt) return "orange darken-2";
      if (isNear) return "orange darken-2";
      return "success";
    },
    getStatusIcon(item, withPending = false) {
      const isOver = withPending
        ? item.isOverLimitWithPending
        : item.isOverLimit;
      const isNear = withPending
        ? item.isNearLimitWithPending
        : item.isNearLimit;
      const isAt = withPending ? item.isAtLimitWithPending : item.isAtLimit;

      if (isOver) return "mdi-alert-circle";
      if (isAt) return "mdi-check-circle";
      if (isNear) return "mdi-alert";
      return "mdi-check";
    },
    getStatusText(item, withPending = false) {
      const isOver = withPending
        ? item.isOverLimitWithPending
        : item.isOverLimit;
      const isNear = withPending
        ? item.isNearLimitWithPending
        : item.isNearLimit;
      const isAt = withPending ? item.isAtLimitWithPending : item.isAtLimit;

      if (isOver) return "Überschritten";
      if (isAt) return "Erreicht";
      if (isNear) return "Fast erreicht";
      return "Verfügbar";
    },
  },
};
</script>

<style scoped>
.v-list-item__subtitle {
  white-space: normal;
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.theme--dark .border-bottom {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.v-list-item {
  transition: background-color 0.2s;
}

.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.theme--dark .v-list-item:hover {
  background-color: rgba(255, 255, 255, 0.02);
}

.v-card__subtitle {
  padding: 12px 16px;
}

.quota-with-pending {
  background-color: rgba(255, 152, 0, 0.02);
}

.theme--dark .quota-with-pending {
  background-color: rgba(255, 152, 0, 0.05);
}
</style>
